import { useMutation } from '@apollo/client';
import { useNotifications } from '@sixfold/common-ui';
import { renderChildren, Children } from '@sixfold/typed-render-props';
import { gql } from 'graphql-tag';
import React from 'react';

import {
  CreateCompanyRelationshipMutation as CreateCompanyRelationshipMutationResult,
  CreateCompanyRelationshipMutationVariables,
  DeleteCompanyRelationshipMutation as DeleteCompanyRelationshipMutationResult,
  DeleteCompanyRelationshipMutationVariables,
  PAGINATION_PAGE_SIZE,
  UpdateCompanyRelationshipSignupStatusMutation as UpdateCompanyRelationshipSignupStatusMutationResult,
  UpdateCompanyRelationshipSignupStatusMutationVariables,
} from '../../lib/graphql';
import { companyRelationshipsListQuery } from '../graphql';

export const createCompanyRelationshipMutation = gql`
  mutation CreateCompanyRelationship($input: CreateCompanyRelationshipInput!) {
    createCompanyRelationship(input: $input) {
      companyRelationshipId
      sourceCompany {
        company_id
        company_name
      }
      targetCompany {
        company_id
        company_name
      }
      relationshipType
    }
  }
`;

const deleteCompanyRelationshipMutation = gql`
  mutation DeleteCompanyRelationship($input: DeleteCompanyRelationshipInput!) {
    deleteCompanyRelationship(input: $input)
  }
`;

const updateCompanyRelationshipSignupStatusMutation = gql`
  mutation UpdateCompanyRelationshipSignupStatus($input: UpdateCompanyRelationshipSignupStatusInput!) {
    updateCompanyRelationshipSignupStatus(input: $input)
  }
`;

export interface DeleteCompanyRelationshipOptions {
  successTitle?: string;
}

interface Props {
  children: Children<{
    createCompanyRelationship: (input: CreateCompanyRelationshipMutationVariables['input']) => Promise<void>;
    deleteCompanyRelationship: (
      companyRelationshipId: string,
      selectedCompanyId: string,
      options?: DeleteCompanyRelationshipOptions,
    ) => Promise<void>;
    updateCompanyRelationshipSignupStatus: (
      input: UpdateCompanyRelationshipSignupStatusMutationVariables['input'],
      selectedCompanyId: string,
    ) => Promise<void>;
  }>;
}

export const CompanyRelationshipMutations: React.FunctionComponent<Props> = (props) => {
  const notify = useNotifications();
  const [createCompanyRelationship] = useMutation<
    CreateCompanyRelationshipMutationResult,
    CreateCompanyRelationshipMutationVariables
  >(createCompanyRelationshipMutation);
  const [deleteCompanyRelationship] = useMutation<
    DeleteCompanyRelationshipMutationResult,
    DeleteCompanyRelationshipMutationVariables
  >(deleteCompanyRelationshipMutation);
  const [updateCompanyRelationshipSignupStatus] = useMutation<
    UpdateCompanyRelationshipSignupStatusMutationResult,
    UpdateCompanyRelationshipSignupStatusMutationVariables
  >(updateCompanyRelationshipSignupStatusMutation);

  return renderChildren(props.children as React.ReactNode, {
    createCompanyRelationship: async (input: CreateCompanyRelationshipMutationVariables['input']) => {
      const response = await createCompanyRelationship({
        variables: {
          input,
        },
      });
      notify.success({
        title: 'Relationship created',
      });

      return response;
    },
    deleteCompanyRelationship: async (
      companyRelationshipId: string,
      selectedCompanyId: string,
      options?: DeleteCompanyRelationshipOptions,
    ) => {
      const response = await deleteCompanyRelationship({
        variables: {
          input: {
            companyRelationshipId,
          },
        },
        refetchQueries: [
          {
            query: companyRelationshipsListQuery,
            variables: {
              companyId: selectedCompanyId,
              limit: PAGINATION_PAGE_SIZE,
            },
          },
        ],
      });
      notify.success({ title: options?.successTitle ?? 'Relationship deleted' });
      return response;
    },
    updateCompanyRelationshipSignupStatus: async (
      input: UpdateCompanyRelationshipSignupStatusMutationVariables['input'],
      selectedCompanyId: string,
    ) => {
      const response = await updateCompanyRelationshipSignupStatus({
        awaitRefetchQueries: true,
        variables: { input },
        refetchQueries: [
          {
            query: companyRelationshipsListQuery,
            variables: {
              companyId: selectedCompanyId,
              limit: PAGINATION_PAGE_SIZE,
            },
          },
        ],
      });
      notify.success({ title: 'Company relationship onboarding status updated' });
      return response;
    },
  });
};
